import format from 'date-fns/format';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  DetailsSidebar,
  Grid,
  Heading,
  Icon,
  Separator,
  Stack,
} from '@eluve/components';
import {
  useAppointmentSessionType,
  useLatestSuperbill,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  FeatureFlaggedComponent,
  GoToPatientButton,
  useUserName,
} from '@eluve/smart-blocks';

import { AppointmentDetailsFragment } from './AppointmentDetails';
import { PatientDetails } from './PatientDetails';
import { SuperbillCard } from './SuperbillCard';

interface ReadonlyAppointmentDetailsProps {
  appointmentId: string;
}

const TimeFormat = 'h:mm a';

const LocationNameFragment = graphql(`
  fragment Location on Locations {
    __typename
    id
    name
  }
`);

export const ReadonlyAppointmentDetails: React.FC<
  ReadonlyAppointmentDetailsProps
> = ({ appointmentId }) => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const { patientId, startDate, endDate, locationId, userId } =
    useCompleteFragment({
      fragment: AppointmentDetailsFragment,
      key: {
        id: appointmentId,
      },
    });

  const locationName = locationId
    ? cacheUtils.readFragment({
        fragment: LocationNameFragment,
        key: {
          id: locationId,
        },
        strict: false,
      })?.name
    : t('appointmentPage.details.noLocationSpecified');

  const username = useUserName(userId);
  const appointmentSessionType = useAppointmentSessionType();

  const { downloadUrl, superbill } = useLatestSuperbill();
  const showArtifacts = superbill != null;

  return (
    <DetailsSidebar title={t('appointmentPage.details.title')}>
      <Stack className="p-4" gap={2}>
        {patientId && (
          <>
            <Stack
              className="border border-b-0 border-transparent px-4 pt-2"
              direction="horizontal"
              align="center"
              gap={3}
            >
              <div className="min-w-0 flex-1">
                <PatientDetails patientId={patientId} />
              </div>
              <FeatureFlaggedComponent flag="PATIENTS_VIEW">
                <GoToPatientButton tenantId={tenantId} patientId={patientId} />
              </FeatureFlaggedComponent>
            </Stack>
            <Separator className="my-2" />
          </>
        )}
        <Stack gap={4} className="px-4 lg:gap-6 lg:px-0">
          <div>
            <Heading size="xl" weight="semibold">
              {t('appointmentPage.details.appointmentHeading')}
            </Heading>
          </div>
          <Grid
            align="center"
            gap={6}
            className="grid-cols-[auto] text-sm lg:grid-cols-[max-content_1fr] lg:gap-y-8"
          >
            <span className="text-text-tertiary">
              {t('appointmentPage.details.date')}
            </span>
            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="Calendar" />
              {startDate ? (
                format(new Date(startDate), 'PP')
              ) : (
                <span>{t('appointmentPage.details.noDateSpecified')}</span>
              )}
            </Stack>
            <span className="text-text-tertiary">
              {t('appointmentPage.details.time')}
            </span>

            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="Clock" />
              <Stack gap={1} wrap direction="horizontal">
                <span>
                  {startDate
                    ? format(new Date(startDate), TimeFormat)
                    : t('appointmentPage.details.startTime')}
                </span>
                <Icon
                  name="MoveHorizontal"
                  size="xl"
                  className="w-3 text-text-tertiary"
                />
                <span>
                  {endDate
                    ? format(new Date(endDate), TimeFormat)
                    : t('appointmentPage.details.end')}
                </span>
              </Stack>
            </Stack>

            <span className="text-text-tertiary">
              {t('appointmentPage.details.location')}
            </span>
            <Stack
              className="truncate border border-transparent text-sm"
              gap={2}
              direction="horizontal"
            >
              <Icon name="MapPin" />
              {locationName ?? t('appointmentPage.details.noLocationSpecified')}
            </Stack>

            <FeatureFlaggedComponent flag="SESSION_TYPES">
              <span className="text-text-tertiary">
                {t('appointmentPage.details.type')}
              </span>
              <Stack
                className="border border-transparent"
                gap={2}
                direction="horizontal"
              >
                <Icon name="Tag" />
                {appointmentSessionType?.name ??
                  t('appointmentPage.details.noType')}
              </Stack>
            </FeatureFlaggedComponent>

            <span className="text-text-tertiary">
              {t('appointmentPage.details.owner')}
            </span>
            <Stack
              className="border border-transparent"
              gap={2}
              direction="horizontal"
            >
              <Icon name="User" />
              <span className="text-text-secondary">{username}</span>
            </Stack>
          </Grid>
        </Stack>

        {showArtifacts && <Separator className="mt-4 mb-2" />}

        {showArtifacts && (
          <Stack gap={4} className="px-2 lg:px-0">
            <div className="px-2 lg:px-0">
              <Heading size="xl" weight="semibold">
                {t('appointmentPage.details.artifacts')}
              </Heading>
            </div>
            <SuperbillCard
              createdAt={superbill.createdAt}
              downloadUrl={downloadUrl}
            />
          </Stack>
        )}
      </Stack>
    </DetailsSidebar>
  );
};
